import { useState, useMemo, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import {
  User, Bell, Shield, Calendar, Mail, HelpCircle,
  ChevronRight, Moon, Sun, Smartphone, LogOut, ExternalLink, Check,
  MessageSquare, Info, Palette, Brain, AtSign, Inbox, Lock, Globe, Lightbulb, ShieldBan, ClipboardList, RefreshCcw, BookOpenText, Trash2, Pencil, ArrowRight
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type {
  SettingsSection, SettingsItem,
  ExpandedContentType
} from './settings-types'
import { apiRequest, invalidateCache } from '../../shared/api/base'
import { useExpandable } from '../../shared/hooks/useExpandable'
import { useConnectionManager } from './useConnectionManager'
import { useSettingsLoader } from './useSettingsLoader'
import { SkeletonCard, SkeletonListItem } from '@tendbot/ui'
import { featureFlags } from '../../featureFlags'
import { getAdminStatus } from '../admin'
import { SectionHeading, AnimatedChevron } from '@tendbot/ui'
import { Badge } from './components'
import { ProfileExpanded, AssistantExpanded, ConnectionExpanded, TogglesExpanded, OwnEmailsExpanded, LanguageExpanded, NudgeSettingsExpanded, BlockedSendersExpanded, NotificationSettingsExpanded, FeedbackModal, DeleteAccountModal, SignOutConfirmModal } from './components'
import ValueMetricsCard from './components/ValueMetricsCard'
import { defaultStagger, expandCollapse } from '@tendbot/ui'

// Not included in MVP — gated behind phase2 feature flag
const PHASE_2_ITEMS = new Set<string>()

const iconMap: Record<string, LucideIcon> = {
  User, Bell, Shield, Calendar, Mail, HelpCircle,
  Moon, Sun, Smartphone, MessageSquare, Info, Palette, Brain,
  AtSign, Inbox, Lock, Globe, Lightbulb, ShieldBan, ClipboardList, RefreshCcw, BookOpenText
}

interface Props {
  onNavigate?: (screen: string) => void
}

export default function Settings({ onNavigate }: Props) {
  const { t, i18n } = useTranslation('settings')
  const { t: tc } = useTranslation('common')
  const { fetchConnections, handleConnectGmail, handleDisconnect, mergeConnectionState } = useConnectionManager()
  const { data, setData, userData, loading, toggles, setToggles } = useSettingsLoader({ fetchConnections, mergeConnectionState })
  const [savingToggle, setSavingToggle] = useState<string | null>(null)
  const expandState = useExpandable()

  const openAssistantEditor = useCallback(() => {
    onNavigate?.('email')
  }, [onNavigate])
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false)
  const [signOutOpen, setSignOutOpen] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)

  useEffect(() => {
    getAdminStatus().then(res => setIsAdmin(res.isAdmin)).catch(() => {})
  }, [])


  const handleToggle = async (toggleId: string, currentValue: boolean): Promise<void> => {
    const newValue = !currentValue
    setSavingToggle(toggleId)

    // Optimistic update
    setToggles(prev => ({ ...prev, [toggleId]: newValue }))

    try {
      // Map toggle IDs to server preference paths
      const prefMap: Record<string, Record<string, unknown>> = {
        'approval-alerts': { notifications: { approval: newValue } },
        'email-notifications': { notifications: { email: newValue } },
        'daily-digest': { notifications: { briefing: newValue } },
      }

      const patch = prefMap[toggleId]
      if (patch) {
        await apiRequest('/settings/preferences', {
          method: 'PATCH',
          body: JSON.stringify(patch),
        })
        invalidateCache('/settings/preferences')
      } else {
        // Device-local settings (appearance, etc.)
        localStorage.setItem(`setting:${toggleId}`, JSON.stringify(newValue))
        if (toggleId === 'bigger-fonts') {
          if (newValue) document.documentElement.dataset.fontSize = 'lg'
          else delete document.documentElement.dataset.fontSize
        }
      }
    } catch (err) {
      // Revert on error
      setToggles(prev => ({ ...prev, [toggleId]: currentValue }))
      console.error('Failed to update setting:', err)
    } finally {
      setSavingToggle(null)
    }
  }

  const { container: containerVariants, item: itemVariants } = defaultStagger

  const sectionsWithLanguage = useMemo(() => {
    const sections: SettingsSection[] = data?.sections || []
    return sections.map(section => {
      if (section.id === 'preferences') {
        const cortexIdx = section.items.findIndex(i => i.id === 'cortex')
        const languageItem: SettingsItem = {
          id: 'language',
          icon: 'Globe',
          label: t('items.language.label'),
          sublabel: i18n.language === 'sv' ? 'Svenska' : 'English',
          hasArrow: true,
          expandable: true,
          expandedContent: { type: 'language' as const }
        }
        const items = [...section.items]
        items.splice(cortexIdx + 1, 0, languageItem)

        // Make notifications item expandable with notification-settings panel
        return { ...section, items: items.map(item =>
          item.id === 'notifications'
            ? { ...item, expandable: true, expandedContent: { type: 'notification-settings' as const } }
            : item
        ) }
      }
      return section
    })
  }, [data?.sections, t, i18n.language])

  if (loading) {
    return (
      <div className="h-full overflow-y-auto px-5 sm:px-8 lg:px-12 py-4">
        <div className="max-w-2xl mx-auto"><SkeletonCard className="h-40 mb-6" />
        <div className="space-y-4">
          <div className="h-4 w-20 bg-charcoal/50 rounded animate-shimmer" />
          <SkeletonListItem />
          <SkeletonListItem />
        </div>
        </div>
      </div>
    )
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="h-full overflow-y-auto px-5 sm:px-8 lg:px-12 py-4"
    >
      <div className="max-w-2xl mx-auto">
      {/* Profile Header */}
      <motion.div
        variants={itemVariants}
        className="bg-gradient-to-r from-gold/30 via-gold/10 to-gold/30 p-px rounded-2xl mb-6"
      >
      <div className="glass-card rounded-2xl p-5 glow-gold">
        <div className="flex items-center gap-4">
          {/* Agent Avatar */}
          <div className="relative">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gold to-gold-dark flex items-center justify-center">
              <span className="font-display text-midnight font-semibold text-2xl">
                {userData?.assistant?.avatar || 'A'}
              </span>
            </div>
            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald rounded-full border-2 border-charcoal flex items-center justify-center">
              <Check size={10} className="text-midnight" />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h2 className="font-display text-xl text-ivory font-semibold">
              {userData?.assistant?.name || 'Your Assistant'}
            </h2>
            <p className="text-pearl/50 text-sm">{t('header.personalAssistant')}</p>
            {userData?.assistant?.email ? (
              <button
                onClick={openAssistantEditor}
                className="mt-1 flex items-center gap-1.5 text-gold/70 hover:text-gold transition-colors group"
                aria-label={t('header.editAssistant')}
              >
                <Mail size={12} className="text-gold/60 shrink-0" />
                <span className="text-xs truncate">{userData.assistant.email}</span>
                <Pencil size={11} className="text-gold/40 group-hover:text-gold transition-colors shrink-0" />
              </button>
            ) : (
              <button
                onClick={openAssistantEditor}
                className="mt-2 flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold/15 border border-gold/30 text-gold text-xs font-medium hover:bg-gold/25 transition-colors"
              >
                <AtSign size={12} className="shrink-0" />
                <span>{t('header.setupEmailCta')}</span>
                <ArrowRight size={12} className="shrink-0" />
              </button>
            )}
          </div>
        </div>

      </div>
      </motion.div>

      {/* Value Metrics — "This week" summary */}
      <motion.div variants={itemVariants}>
        <ValueMetricsCard />
      </motion.div>

      {/* Settings Sections */}
      {sectionsWithLanguage.map((section: SettingsSection) => (
        <motion.div key={section.id} variants={itemVariants} className="mb-6">
          <SectionHeading>{t(`sections.${section.id}`)}</SectionHeading>
          <div className="glass-card rounded-xl overflow-hidden divide-y divide-slate-mid/30">
            {section.items.map((item: SettingsItem) => {
              const Icon: LucideIcon = iconMap[item.icon] || User
              const isComingSoon = !featureFlags.phase2 && PHASE_2_ITEMS.has(item.id)
              const isExpanded = expandState.isExpanded(item.id)
              const hasExpandableContent = item.expandable && item.expandedContent

              return (
                <div key={item.id} id={`settings-item-${item.id}`}>
                  {/* Main row — uses div instead of button to allow nested Connect button */}
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      if (item.id === 'feedback') {
                        setFeedbackOpen(true)
                      } else if (item.navigate) {
                        onNavigate?.(item.navigate)
                      } else if (hasExpandableContent) {
                        expandState.toggle(item.id)
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        if (item.id === 'feedback') setFeedbackOpen(true)
                        else if (item.navigate) onNavigate?.(item.navigate)
                        else if (hasExpandableContent) expandState.toggle(item.id)
                      }
                    }}
                    className="w-full flex items-center gap-3 p-4 transition-colors text-left group hover:bg-slate-mid/30 cursor-pointer"
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                      item.status?.connected ? 'bg-emerald/10' : 'bg-slate-mid/50'
                    }`}>
                      <Icon size={18} className={item.status?.connected ? 'text-emerald' : 'text-pearl/60'} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-ivory text-sm font-medium">{t(`items.${item.id}.label`, item.label)}</span>
                        {isComingSoon && (
                          <Badge>{tc('status.comingSoon')}</Badge>
                        )}
                        {item.badge && !isComingSoon && (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${item.badge.color || 'bg-emerald/15 text-emerald'}`}>
                            {item.badge.text}
                          </span>
                        )}
                      </div>
                      {item.sublabel && (
                        <span className="text-pearl/50 text-xs truncate block">{item.sublabel}</span>
                      )}
                    </div>

                    {item.status?.connected && (
                      <div className="w-5 h-5 rounded-full bg-emerald/20 flex items-center justify-center mr-1">
                        <div className="w-2 h-2 rounded-full bg-emerald" />
                      </div>
                    )}

                    {hasExpandableContent ? (
                      <AnimatedChevron isOpen={isExpanded} size={16} className="text-pearl/55 group-hover:text-gold/60 transition-colors" />
                    ) : item.hasArrow && (
                      item.external ? (
                        <ExternalLink size={16} className="text-pearl/55 group-hover:text-gold/60 transition-colors" />
                      ) : (
                        <ChevronRight size={16} className="text-pearl/55 group-hover:text-gold/60 transition-colors" />
                      )
                    )}
                  </div>

                  {/* Expanded content */}
                  <AnimatePresence>
                    {isExpanded && hasExpandableContent && (
                      <motion.div
                        {...expandCollapse}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 pb-4 pt-0 border-t border-slate-mid/30">
                          <div className="pt-3 space-y-3">
                            {item.expandedContent!.type === 'profile' && (
                              <ProfileExpanded />
                            )}

                            {item.expandedContent!.type === 'assistant' && (
                              <AssistantExpanded
                                initialName={item.expandedContent!.name}
                                initialEmail={item.expandedContent!.email}
                              />
                            )}

                            {item.expandedContent!.type === 'connection' && (
                              <ConnectionExpanded
                                content={item.expandedContent as Extract<ExpandedContentType, { type: 'connection' }>}
                                toggles={toggles}
                                savingToggle={savingToggle}
                                onToggle={handleToggle}
                                onConnectGmail={handleConnectGmail}
                                onDisconnect={() => handleDisconnect(item.id, data, setData)}
                              />
                            )}

                            {item.expandedContent!.type === 'toggles' && (
                              <TogglesExpanded
                                content={item.expandedContent as Extract<ExpandedContentType, { type: 'toggles' }>}
                                toggles={toggles}
                                savingToggle={savingToggle}
                                onToggle={handleToggle}
                              />
                            )}

                            {item.expandedContent!.type === 'own-emails' && (
                              <OwnEmailsExpanded
                                content={item.expandedContent as { type: 'own-emails'; emails: string[]; loginEmail: string }}
                              />
                            )}

                            {item.expandedContent!.type === 'language' && (
                              <LanguageExpanded />
                            )}

                            {item.expandedContent!.type === 'nudge-settings' && (
                              <NudgeSettingsExpanded />
                            )}

                            {item.expandedContent!.type === 'blocked-senders' && (
                              <BlockedSendersExpanded
                                content={item.expandedContent as { type: 'blocked-senders'; entries: string[] }}
                              />
                            )}

                            {item.expandedContent!.type === 'notification-settings' && (
                              <NotificationSettingsExpanded />
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )
            })}
          </div>
        </motion.div>
      ))}

      {/* Admin Section */}
      {isAdmin && (
        <motion.div variants={itemVariants} className="mb-6">
          <SectionHeading>{t('sections.admin')}</SectionHeading>
          <div className="glass-card rounded-xl overflow-hidden">
            <div
              role="button"
              tabIndex={0}
              onClick={() => onNavigate?.('admin')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onNavigate?.('admin')
                }
              }}
              className="w-full flex items-center gap-3 p-4 transition-colors text-left group hover:bg-slate-mid/30 cursor-pointer"
            >
              <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-gold/10">
                <Shield size={18} className="text-gold" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-ivory text-sm font-medium">{t('items.admin.label')}</span>
                <span className="text-pearl/50 text-xs block">{t('items.admin.sublabel')}</span>
              </div>
              <ChevronRight size={16} className="text-pearl/55 group-hover:text-gold/60 transition-colors" />
            </div>
          </div>
        </motion.div>
      )}

      {/* Logout Button */}
      <motion.button
        variants={itemVariants}
        onClick={() => setSignOutOpen(true)}
        className="w-full py-3 rounded-xl bg-rose/10 border border-rose/20 text-rose font-medium text-sm hover:bg-rose/20 transition-colors flex items-center justify-center gap-2 mb-6"
      >
        <LogOut size={16} />
        {t('signOut')}
      </motion.button>

      {/* Danger Zone */}
      <motion.div variants={itemVariants} className="mb-8">
        <SectionHeading>{t('sections.dangerZone')}</SectionHeading>
        <div className="rounded-xl border border-rose/20 overflow-hidden">
          <div
            role="button"
            tabIndex={0}
            onClick={() => setDeleteAccountOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setDeleteAccountOpen(true)
              }
            }}
            className="w-full flex items-center gap-3 p-4 transition-colors text-left group hover:bg-rose/5 cursor-pointer"
          >
            <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-rose/10">
              <Trash2 size={18} className="text-rose/60" />
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-ivory text-sm font-medium">{t('deleteAccount.button')}</span>
              <span className="text-pearl/50 text-xs block">{t('deleteAccount.sublabel')}</span>
            </div>
            <ChevronRight size={16} className="text-pearl/55 group-hover:text-rose/40 transition-colors" />
          </div>
        </div>
      </motion.div>

      {/* Footer */}
      <motion.div variants={itemVariants} className="text-center pb-4">
        <img src="/tendbot.png" alt="" className="h-8 w-auto mx-auto mb-1.5 opacity-30" />
        <p className="text-pearl/50 text-xs">TendBot v1.0.0</p>
        <p className="text-pearl/50 text-xs mt-1">{t('footer.tagline')}</p>
      </motion.div>
      </div>

      <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
      <DeleteAccountModal open={deleteAccountOpen} onClose={() => setDeleteAccountOpen(false)} />
      <SignOutConfirmModal open={signOutOpen} onClose={() => setSignOutOpen(false)} />
    </motion.div>
  )
}
